/*
 * Support intake: the in-app "Message support" form seam.
 *
 * Duncan ruling CMT-2026-10-31-002 (amends PRD-SUPPORT-EMAIL-SENDER-IDENTITY-V1
 * D1): every support entry point in my.trumin opens a ticket through settings'
 * POST /api/participant/support (contract MY-TRUMIN-SUPPORT-ROUTE-CONTRACT-1031).
 * There is no mailto and no support mailbox is shown anywhere: the old address
 * reached a mailbox, not the support queue.
 *
 *  - HOST: the same manage API host as participant-config (VITE_SETTINGS_API_BASE).
 *    Unset ⇒ supportAvailable() is false and callers show the honest not-wired
 *    state; no host is ever guessed.
 *  - AUTH: Bearer = the Cognito ID token (NOT the access token: identity is read
 *    from the token's email/name claims, and an access token has no email). The
 *    body never carries identity; the route rejects it (400 identity_from_token).
 *  - CONTEXT: event / order / step / url ride along so support needs no
 *    interrogation. The url is origin + path only, with link tokens redacted
 *    (claim, transfer and guardian-sign links carry secrets in the path).
 *
 * One form, mounted once at the app root (SupportFormHost); entry points call
 * openSupportForm() with their own prefill. A 201 is the only success signal:
 * any other answer means nothing was delivered and the draft stays editable.
 */

export type MismatchReason = 'gender' | 'age' | null

/** SUPPORT_TOPICS v1 — 1:1 with settings' route-per-topic constant (contract
 *  SUPPORT-TOPICS-ROUTE-CONTRACT-1031, Duncan ruling CMT-2026-10-31-005).
 *  settings owns the keys; the server 400s an unknown one. Order = the
 *  contract table (event-routed first, then Trumin CS). The old `event` key
 *  is a server-side alias only — this form sends `event_info`. */
export const SUPPORT_TOPICS = [
  'ticket_type', 'waves', 'refund', 'event_info', 'transfer', 'order',
  'account', 'payment', 'bug', 'other',
] as const
export type SupportTopic = (typeof SUPPORT_TOPICS)[number]

export const SUPPORT_MESSAGE_MAX = 5000

/** Context an entry point knows about. Every field is optional. */
export interface SupportContext {
  topic?: SupportTopic
  /** Starting text for the message box (the buyer can edit it). */
  message?: string
  /** Event UUID. A non-UUID value is dropped (the route would 400 it). */
  eventId?: string
  orderRef?: string
  ticketLabel?: string
  step?: string
  url?: string
}

/** The manage API host, or null (honest absence — never guessed). */
export function supportApiBase(env: Record<string, string | undefined> = import.meta.env): string | null {
  return env.VITE_SETTINGS_API_BASE || null
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Paths whose segment after the prefix is a link secret: never sent as-is.
const TOKEN_PATH_RE = /^\/(claim|transfer|guardian-sign)\/[^/]+/

const PATH_CONTEXT: { re: RegExp; step: string; order?: number; event?: number }[] = [
  { re: /^\/orders\/([^/]+)\/tickets\/[^/]+\/seats\/[^/]+\/assign/, step: 'orders.assign', order: 1 },
  { re: /^\/orders\/([^/]+)\/tickets\/[^/]+/, step: 'orders.ticket', order: 1 },
  { re: /^\/orders\/([^/]+)/, step: 'orders.detail', order: 1 },
  { re: /^\/orders$/, step: 'orders' },
  { re: /^\/get-ready\/(waiver|heat|child-confirm|questionnaire)\/([^/]+)/, step: 'get-ready.$1', event: 2 },
  { re: /^\/get-ready\/profile/, step: 'get-ready.profile' },
  { re: /^\/get-ready/, step: 'get-ready' },
  { re: /^\/tickets/, step: 'tickets' },
  { re: /^\/profile/, step: 'profile' },
  { re: /^\/referrals/, step: 'referrals' },
  { re: /^\/claim\//, step: 'claim' },
  { re: /^\/transfer\//, step: 'transfer.landing' },
  { re: /^\/guardian-sign\//, step: 'guardian-sign' },
]

function decodeSegment(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

/** Origin + path, no query or hash, with link tokens replaced. */
export function supportSafeUrl(origin: string, pathname: string): string {
  const path = pathname.replace(TOKEN_PATH_RE, (_m, kind: string) => `/${kind}/[link]`)
  return `${origin}${path}`
}

/** Derive step / order / event / url from where the buyer is in the app. */
export function supportContextFromLocation(
  loc: { origin: string; pathname: string } = window.location,
): SupportContext {
  const pathname = loc.pathname.replace(/\/+$/, '') || '/'
  const ctx: SupportContext = { url: supportSafeUrl(loc.origin, pathname) }
  for (const p of PATH_CONTEXT) {
    const m = pathname.match(p.re)
    if (!m) continue
    ctx.step = p.step.replace('$1', m[1] ?? '')
    if (p.order) ctx.orderRef = decodeSegment(m[p.order])
    if (p.event && UUID_RE.test(m[p.event])) ctx.eventId = m[p.event]
    break
  }
  return ctx
}

/** The default topic for a step when the entry point names none. */
export function defaultTopicFor(step: string | undefined): SupportTopic {
  if (!step) return 'other'
  if (step.startsWith('orders')) return 'order'
  if (step.startsWith('transfer')) return 'transfer'
  if (step.startsWith('get-ready')) return 'event_info'
  return 'other'
}

const clip = (v: string | undefined, max: number): string | undefined => {
  const t = v?.trim()
  return t ? t.slice(0, max) : undefined
}

/** The request body: only contract keys, trimmed and within limits, no identity. */
export function buildSupportBody(topic: SupportTopic, message: string, ctx: SupportContext): Record<string, string> {
  const body: Record<string, string> = { topic, message: message.trim() }
  const eventId = ctx.eventId?.trim()
  if (eventId && UUID_RE.test(eventId)) body.event_id = eventId
  const optional: [string, string | undefined][] = [
    ['order_ref', clip(ctx.orderRef, 64)],
    ['ticket_label', clip(ctx.ticketLabel, 200)],
    ['step', clip(ctx.step, 128)],
    ['url', clip(ctx.url, 2048)],
  ]
  for (const [k, v] of optional) if (v) body[k] = v
  return body
}

export type SupportResult =
  | { kind: 'sent'; reference: string | null }
  | { kind: 'invalid'; field: string; code: string }
  | { kind: 'auth'; identity: boolean }
  | { kind: 'rate_limited'; retryAfterSec: number | null }
  | { kind: 'unavailable' }
  | { kind: 'error' }

async function readJson(res: Response): Promise<Record<string, unknown>> {
  try {
    const j: unknown = await res.json()
    return j && typeof j === 'object' ? (j as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** POST the message. Only a 201 is success; every other answer maps to a state. */
export async function postSupportMessage(
  base: string,
  idToken: string,
  body: Record<string, string>,
  fetchImpl: typeof fetch = fetch,
): Promise<SupportResult> {
  let res: Response
  try {
    res = await fetchImpl(`${base.replace(/\/+$/, '')}/api/participant/support`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(body),
    })
  } catch {
    return { kind: 'error' }
  }
  const j = await readJson(res)
  if (res.status === 201) {
    return { kind: 'sent', reference: typeof j.reference === 'string' && j.reference ? j.reference : null }
  }
  if (res.status === 400) {
    return {
      kind: 'invalid',
      field: typeof j.field === 'string' ? j.field : 'body',
      code: typeof j.code === 'string' ? j.code : 'invalid',
    }
  }
  if (res.status === 401) return { kind: 'auth', identity: j.error === 'identity_unavailable' }
  if (res.status === 429) {
    const n = Number(res.headers.get('Retry-After'))
    return { kind: 'rate_limited', retryAfterSec: Number.isFinite(n) && n > 0 ? Math.ceil(n) : null }
  }
  if (res.status === 503) return { kind: 'unavailable' }
  return { kind: 'error' }
}

// ---------------------------------------------------------------------------
// Open bus: entry points ask the one mounted form to open.
// ---------------------------------------------------------------------------

/** One of the customer's OWN tickets, offered by the general Help entry. */
export interface SupportEventChoice {
  eventId: string
  /** What the customer sees, e.g. "Apex Trail Run — 10K". */
  label: string
  /** Sent as ticket_label (e.g. the ticket type). */
  ticketLabel?: string
  /** Sent as order_ref when the choice comes from an order (bought, seat not assigned). */
  orderRef?: string
}

export interface SupportOpenRequest extends SupportContext {
  /** Called once with the SUP- reference after a 201. */
  onSent?: (reference: string | null) => void
  /**
   * GENERAL entry (Duncan ruling CMT-2026-10-31-005 b): the form asks "Which
   * event is this about?" and lists ONLY these (the customer's own tickets)
   * plus "My account / something else" (no event). When present, the event
   * comes ONLY from that answer — never from the page or the email. A ticket
   * entry point leaves this unset and passes its own eventId instead.
   */
  eventChoices?: SupportEventChoice[]
}

/** The general-entry choices: one per ticket from the customer's own get-ready
 *  cards (event — ticket type, — holder for a child/guardian seat), THEN one per
 *  event the customer BOUGHT that has no card yet (seats not assigned; labelled
 *  event — order ref). The server routes on the same rule: any order the
 *  customer placed counts as "held" (event_customer, quality G-support 1101), so
 *  a buyer with unassigned seats must be able to pick that event. UUID events
 *  only. */
export function supportEventChoices(
  cards: ReadonlyArray<{ id: string; name: string; ticketId?: string; ticketTypeName?: string | null; holderName?: string | null; childContext?: boolean }>,
  orders: ReadonlyArray<{ eventId: string; ref: string; eventName?: string | null }> = [],
): SupportEventChoice[] {
  const seen = new Set<string>()
  const out: SupportEventChoice[] = []
  for (const c of cards) {
    if (!UUID_RE.test(c.id)) continue
    const key = c.ticketId ? `t:${c.ticketId}` : `e:${c.id}`
    if (seen.has(key)) continue
    seen.add(key)
    const type = c.ticketTypeName?.trim() || undefined
    const holder = c.childContext ? c.holderName?.trim() || undefined : undefined
    out.push({
      eventId: c.id,
      label: [c.name, type, holder].filter(Boolean).join(' — '),
      ...(type ? { ticketLabel: type } : {}),
    })
  }
  const carded = new Set(out.map((o) => o.eventId))
  for (const o of orders) {
    if (!UUID_RE.test(o.eventId) || carded.has(o.eventId)) continue
    carded.add(o.eventId)
    const name = o.eventName?.trim()
    out.push({
      eventId: o.eventId,
      label: [name || 'Your order', o.ref].filter(Boolean).join(' — '),
      orderRef: o.ref,
    })
  }
  return out
}

type SupportListener = (req: SupportOpenRequest) => void
const listeners = new Set<SupportListener>()

/** Register the form host. Returns the unsubscribe. */
export function subscribeSupportForm(listener: SupportListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Is the support form usable in this build (host configured)? */
export function supportAvailable(env: Record<string, string | undefined> = import.meta.env): boolean {
  return supportApiBase(env) !== null
}

/**
 * Open the support form with this prefill. Returns false (nothing opened) when
 * the API host is not configured or no form is mounted; callers then show the
 * honest not-wired state.
 */
export function openSupportForm(
  req: SupportOpenRequest = {},
  env: Record<string, string | undefined> = import.meta.env,
): boolean {
  if (!supportAvailable(env) || listeners.size === 0) return false
  for (const l of listeners) l(req)
  return true
}

// ---------------------------------------------------------------------------
// Eligibility reported-state store (ADDENDUM sub-state 2: banner+button →
// "support request submitted", no re-nag). DEVICE-LOCAL, keyed PER TICKET so a
// support-side ticket swap starts clean on the new ticket. Written only after
// the route answered 201, and it records the SUP- reference it returned.
// ---------------------------------------------------------------------------

const REPORTS_KEY = 'my.eligibilitySupportReports.v1'

export interface EligibilityReport {
  /** Machine axis reported (gender|age|null when the server sent none). */
  reason: MismatchReason
  /** ISO timestamp of the report — the participant-visible reference date. */
  at: string
  /** The SUP- reference from the route (absent on pre-route records). */
  reference?: string
}

function readReports(): Record<string, EligibilityReport> {
  try {
    const raw = localStorage.getItem(REPORTS_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, EligibilityReport>) : {}
  } catch {
    return {}
  }
}

function writeReports(reports: Record<string, EligibilityReport>): void {
  try {
    localStorage.setItem(REPORTS_KEY, JSON.stringify(reports))
  } catch {
    // Persistence unavailable (private mode etc.) — the in-session state the
    // caller holds still applies; nothing is silently invented.
  }
}

/** The recorded report for a ticket, or null (never fabricated). */
export function eligibilityReport(ticketId: string): EligibilityReport | null {
  return readReports()[ticketId] ?? null
}

/** Record a support message the route accepted (201) for this ticket. */
export function markEligibilityReported(
  ticketId: string,
  reason: MismatchReason,
  reference: string | null = null,
  now: () => Date = () => new Date(),
): EligibilityReport {
  const report: EligibilityReport = { reason, at: now().toISOString() }
  if (reference) report.reference = reference
  const reports = readReports()
  reports[ticketId] = report
  writeReports(reports)
  return report
}

/** Test hygiene. */
export function clearEligibilityReports(): void {
  try {
    localStorage.removeItem(REPORTS_KEY)
  } catch {
    // nothing to clear
  }
}
